/**
 * A book planned for an export of its text (parts 20, 22): each chapter parsed once into its tree and model, and every
 * image reference the text or the cover names, each once, so the worker can load them before the documents are written.
 * The EPUB and the DOCX both start here.
 */
import { chapterModel, imageDestination, parseChapter, readImageDestination } from '@cockatrice/core';
import type { Block, ChapterModel, ImageReference, Inline, ParsedChapter } from '@cockatrice/core';
import { headingImage } from '@cockatrice/layout';
import type { PdfBook } from '@cockatrice/layout';

/** A chapter parsed once: its title, its text's tree and its model. */
export interface PlannedChapter {
  readonly title: string;
  readonly subtitle: string;
  /** T‴2: its own image reference; empty for none. */
  readonly image: string;
  readonly parsed: ParsedChapter;
  readonly model: ChapterModel;
}

/** The book, its chapters parsed, and every image reference the text or the cover names (part 20, I1–I6), each once. */
export interface BookPlan {
  readonly book: PdfBook;
  readonly chapters: readonly PlannedChapter[];
  readonly references: readonly ImageReference[];
}

/** Every image destination in the blocks, as `imageDestinations` finds them for the layout (part 16b, L5). */
function destinationsIn(model: ChapterModel, found: Set<string>): void {
  const inline = (items: readonly Inline[]): void => {
    for (const item of items) {
      if (item.kind === 'image') found.add(item.destination);
      else if (item.kind === 'brackets') inline(item.text);
      else if ('children' in item) inline(item.children);
    }
  };
  const blocks = (list: readonly Block[]): void => {
    for (const block of list) {
      if (block.kind === 'paragraph' || block.kind === 'heading' || block.kind === 'centered') inline(block.content);
      else if (block.kind === 'quote' || block.kind === 'footnote') blocks(block.blocks);
      else if (block.kind === 'list') for (const item of block.items) blocks(item);
    }
  };
  blocks(model.blocks);
}

/** Parses every chapter once and lists the image references to load (the text's, then the cover's). */
export function planBook(book: PdfBook): BookPlan {
  const found = new Set<string>();
  const chapters = book.chapters.map((chapter): PlannedChapter => {
    const parsed = parseChapter(chapter.text);
    const model = chapterModel(parsed);
    destinationsIn(model, found);
    // T‴: the heading's image, like any in the text, is one of the book's to load.
    const heading = headingImage({ theme: book.theme, image: chapter.image });
    if (heading !== '') found.add(heading);
    return { title: chapter.title, subtitle: chapter.subtitle, image: chapter.image, parsed, model };
  });
  const { cover } = book.metadata;
  if (cover !== null) found.add(imageDestination(cover));
  const references = [...found].flatMap((destination) => {
    const reference = readImageDestination(destination);
    return reference === null ? [] : [reference];
  });
  return { book, chapters, references };
}
